import { filmTypes, shortFilms, type FilmType, type ShortFilm } from "@/mocks/shortFilms";
import { newsList, type NewsCategory, type NewsItem } from "@/mocks/news";

export const REFERENCE_DATE = new Date("2026-10-17T00:00:00");

export type DurationBucket = "all" | "short" | "medium" | "long";
export type DateRange = "all" | "1m" | "3m";
export type FilmSort = "latest" | "popular" | "duration";

export interface FilmFilterOptions {
  type?: FilmType | "all";
  duration?: DurationBucket;
  range?: DateRange;
  sort?: FilmSort;
}

function daysBetween(from: Date, to: Date): number {
  const diff = from.getTime() - to.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export function sortFilms(list: ShortFilm[], sort: FilmSort): ShortFilm[] {
  const sorted = [...list];
  if (sort === "popular") {
    sorted.sort((a, b) => b.views - a.views);
  } else if (sort === "duration") {
    sorted.sort((a, b) => a.duration - b.duration);
  } else {
    sorted.sort((a, b) => b.publishDate.localeCompare(a.publishDate));
  }
  return sorted;
}

export function filterFilms(options: FilmFilterOptions = {}): ShortFilm[] {
  const { type = "all", duration = "all", range = "all", sort = "latest" } = options;

  const filtered = shortFilms.filter((film) => {
    if (type !== "all" && film.type !== type) return false;

    if (duration === "short" && film.duration > 5) return false;
    if (duration === "medium" && (film.duration <= 5 || film.duration > 15)) return false;
    if (duration === "long" && film.duration <= 15) return false;

    if (range !== "all") {
      const days = daysBetween(REFERENCE_DATE, new Date(`${film.publishDate}T00:00:00`));
      const limit = range === "1m" ? 31 : 92;
      if (days > limit) return false;
    }

    return true;
  });

  return sortFilms(filtered, sort);
}

export function newsSortLatest(list: NewsItem[]): NewsItem[] {
  return [...list].sort((a, b) => b.publishDate.localeCompare(a.publishDate));
}

export function filterNews(category: NewsCategory | "all", range: DateRange = "all"): NewsItem[] {
  const filtered = newsList.filter((item) => {
    if (category !== "all" && item.category !== category) return false;
    if (range !== "all") {
      const days = daysBetween(REFERENCE_DATE, new Date(`${item.publishDate}T00:00:00`));
      const limit = range === "1m" ? 31 : 92;
      if (days > limit) return false;
    }
    return true;
  });

  return newsSortLatest(filtered);
}

function normalize(text: string): string {
  return text.toLowerCase().trim();
}

export function searchFilms(keyword: string): ShortFilm[] {
  const word = normalize(keyword);
  if (!word) return [];
  return shortFilms.filter((film) =>
    [film.title, film.tagline, film.synopsis, film.director, film.type, ...film.tags]
      .join(" ")
      .toLowerCase()
      .includes(word),
  );
}

export function searchNews(keyword: string): NewsItem[] {
  const word = normalize(keyword);
  if (!word) return [];
  return newsSortLatest(
    newsList.filter((item) =>
      [item.title, item.summary, item.author, item.category, ...item.content]
        .join(" ")
        .toLowerCase()
        .includes(word),
    ),
  );
}

export function getFilmById(id: string | undefined): ShortFilm | undefined {
  if (!id) return undefined;
  return shortFilms.find((film) => film.id === id);
}

export function getNewsById(id: string | undefined): NewsItem | undefined {
  if (!id) return undefined;
  return newsList.find((item) => item.id === id);
}

export function relatedFilms(film: ShortFilm, count = 4): ShortFilm[] {
  const scored = shortFilms
    .filter((item) => item.id !== film.id)
    .map((item) => {
      let score = 0;
      if (item.type === film.type) score += 4;
      if (item.director === film.director) score += 3;
      score += item.tags.filter((tag) => film.tags.includes(tag)).length * 2;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views);

  return scored.slice(0, count).map((entry) => entry.item);
}

export function relatedNews(item: NewsItem, count = 4): NewsItem[] {
  const scored = newsList
    .filter((entry) => entry.id !== item.id)
    .map((entry) => {
      let score = 0;
      if (entry.category === item.category) score += 4;
      const words = item.title.split(/[\s，：、·]+/).filter((word) => word.length > 1);
      score += words.filter((word) => entry.title.includes(word)).length * 2;
      return { entry, score };
    })
    .sort((a, b) => b.score - a.score || b.entry.publishDate.localeCompare(a.entry.publishDate));

  return scored.slice(0, count).map((entry) => entry.entry);
}

export function countFilmsByType(type: FilmType): number {
  return shortFilms.filter((film) => film.type === type).length;
}

export function countNewsByCategory(category: NewsCategory): number {
  return newsList.filter((item) => item.category === category).length;
}

export { filmTypes, shortFilms, newsList };
export type { FilmType, ShortFilm, NewsCategory, NewsItem };